/// <amd-module name="VSSUI/Components/VssBreadcrumb/VssBreadcrumb" />

import { IBreadcrumbItem } from "OfficeFabric/Breadcrumb";
import { IconButton } from "OfficeFabric/Button";
import { Callout, DirectionalHint } from "OfficeFabric/Callout";
import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { ILink, Link } from "OfficeFabric/Link";
import { ResizeGroup } from "OfficeFabric/ResizeGroup";
import { BaseComponent, createRef, EventGroup, findIndex } from "OfficeFabric/Utilities";
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { Icon } from "VSSUI/Icon";
import { ItemIndicatorSet } from "VSSUI/ItemIndicator";
import { IItemPickerProvider, IPickList, PICKER_CHANGE_EVENT, PickList } from "VSSUI/PickList";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, getSafeIdSelector, KeyCode } from "VSSUI/Util";
import * as VSSUIResources from "VSSUI/VSS.Resources.VSSUI";
import { IVssIconProps, VssIcon, VssIconType } from "VSSUI/VssIcon";
import {
    IDividerAsProps, IInternalBreadcrumbItem, IInternalBreadcrumbProps, IPickerDropdownProps,
    IVssBreadcrumb, IVssBreadcrumbItem,
    IVssBreadcrumbItemInternal, IVssBreadcrumbPickList,
    IVssBreadcrumbProps, VssBreadcrumbDividerType,
    VssBreadcrumbStyles
} from "../../Components/VssBreadcrumb/VssBreadcrumb.Props";

import "VSS/LoaderPlugins/Css!VSSUI/Components/VssBreadcrumb/VssBreadcrumb";

const DEFAULT_MAXDISPLAY_ITEMS = 4;

/** @deprecated */
export interface IPickerDropdownState {
    calloutIsVisible: boolean;
    itemPicker?: IItemPickerProvider;
    events: EventGroup;
    updateDelegate: () => void;
}

class PickerDropdown extends BaseComponent<IPickerDropdownProps, IPickerDropdownState> implements IVssBreadcrumbPickList {
    private _pickList: IPickList;

    constructor(props: IPickerDropdownProps) {
        super(props);

        this.state = { calloutIsVisible: false, itemPicker: props.item.itemPicker, events: this._events, updateDelegate: () => this.forceUpdate() };
    }

    public render(): JSX.Element | null {
        const { getPinElement, item, width, minWidth, maxWidth, maxHeight, pickListClassName } = this.props;

        if (this.state.calloutIsVisible) {
            const itemPickerProvider = item.itemPicker;

            return (
                <Callout isBeakVisible={false} target={getPinElement()} onPositioned={this._onPositioned} onDismiss={this._onDismiss}>
                    <PickList
                        className={css("picker-dropdown", pickListClassName)}
                        ref={(pickList: PickList) => (this._pickList = pickList)}
                        getActions={() => itemPickerProvider.actions}
                        getListItem={itemPickerProvider.getListItem}
                        groups={itemPickerProvider.groups}
                        pivots={itemPickerProvider.pivots}
                        indicators={itemPickerProvider.dropdownIndicators}
                        initiallySelectedItems={itemPickerProvider.selectedItem ? [itemPickerProvider.selectedItem] : undefined}
                        isSearchable={itemPickerProvider.isSearchable}
                        items={itemPickerProvider.getItems()}
                        searchResultsLoadingText={itemPickerProvider.searchResultsLoadingText}
                        noItemsText={itemPickerProvider.noItemsText}
                        itemAriaDescription={itemPickerProvider.itemAriaDescription}
                        searchNoResultsText={itemPickerProvider.searchNoResultsText}
                        searchResultsGroupName={itemPickerProvider.searchResultsGroupName}
                        searchTextPlaceholder={itemPickerProvider.searchTextPlaceholder}
                        shouldVirtualize={items => {
                            return (items || []).length > 50;
                        }}
                        width={width}
                        minWidth={minWidth}
                        maxWidth={maxWidth}
                        maxHeight={maxHeight}
                        onFocusLost={this._onDismiss}
                        onSearch={itemPickerProvider.onSearch}
                        onSelectionChanged={item.onSelectionChanged}
                        onRenderItemText={itemPickerProvider.onRenderItemText}
                        onSelectedPivotChanged={itemPickerProvider.onSelectedPivotChanged}
                    />
                </Callout>
            );
        }
        return null;
    }

    public componentDidMount() {
        if (this.props.item.isHeaderPickerItem && this.props.item.itemPicker) {
            this.props.item.itemPicker;
            this._events.on(this.props.item.itemPicker, PICKER_CHANGE_EVENT, this.state.updateDelegate);
        }
    }

    public componentWillUnmount() {
        if (this.props.item.isHeaderPickerItem && this.props.item.itemPicker) {
            this._events.off(this.props.item.itemPicker, PICKER_CHANGE_EVENT, this.state.updateDelegate);
        }
    }

    public componentWillReceiveProps(nextProps: IPickerDropdownProps) {
        if (this.props.item.isHeaderPickerItem && this.props.item.itemPicker && this.props.item.itemPicker !== nextProps.item.itemPicker) {
            this._events.off(this.props.item.itemPicker, PICKER_CHANGE_EVENT, this.state.updateDelegate);
            this._events.on(nextProps.item.itemPicker, PICKER_CHANGE_EVENT, this.state.updateDelegate);
        }
    }

    public openDropdown() {
        if (this.props.onPickListOpened) {
            this.props.onPickListOpened();
        }
        this.setState({ calloutIsVisible: true });
    }

    public closeDropdown() {
        this.setState({ calloutIsVisible: false });
        if (this.props.onPickListClosed) {
            this.props.onPickListClosed();
        }
    }

    public toggleDropdown() {
        if (this.state.calloutIsVisible) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    private _onPositioned = () => {
        this._pickList.focus();
    };

    private _onDismiss = () => {
        this.closeDropdown();
    };
}

/** @deprecated */
export interface IVssBreadcrumbState {
    pickerIsOpen: boolean;
    pickerItemKey: string | null;
    displayedItems: IVssBreadcrumbItem[];
    linkItems: IBreadcrumbItem[];
}

/** @deprecated Please use import { Breadcrumb } from "VSSUI/Breadcrumb" */
export class VssBreadcrumb extends React.Component<IVssBreadcrumbProps, IVssBreadcrumbState> implements IVssBreadcrumb {
    private _pinElements: { [key: string]: HTMLElement } = {};
    private _linkElements: { [key: string]: ILink } = {};
    private _breadcrumbElements: { [key: string]: HTMLElement } = {};
    private _focusedLinkIndex: number = -1;

    public pickList: IVssBreadcrumbPickList;

    public static defaultProps: IVssBreadcrumbProps = {
        items: []
    };

    constructor(props: IVssBreadcrumbProps) {
        super(props);

        const displayedItems = VssBreadcrumb._arrangeItems(props.items);

        this.state = {
            pickerIsOpen: false,
            pickerItemKey: null,
            displayedItems: displayedItems,
            linkItems: VssBreadcrumb._getLinkItems(displayedItems)
        };
    }

    public static getDerivedStateFromProps(props: IVssBreadcrumbProps, state: IVssBreadcrumbState): IVssBreadcrumbState {
        const displayedItems = VssBreadcrumb._arrangeItems(props.items);
        return { ...state, displayedItems, linkItems: VssBreadcrumb._getLinkItems(displayedItems) };
    }

    public componentDidUpdate(prevProps: IVssBreadcrumbProps) {
        if (!prevProps.extraContentSelected && this.props.extraContentSelected) {
            this._focusedLinkIndex = this.state.linkItems.length;
        } else if (prevProps.extraContentSelected && !this.props.extraContentSelected && this._focusedLinkIndex === this.state.linkItems.length) {
            this._focusedLinkIndex = -1;
        }
    }

    private static _getLinkItems(displayedItems: IVssBreadcrumbItem[]): IBreadcrumbItem[] {
        return displayedItems.filter(item => {
            return (item.isHeaderPickerItem && item.itemPicker) || item.href || item.onClick;
        });
    }

    private _getIcon(iconProps: IVssIconProps | undefined) {
        if (iconProps) {
            return <VssIcon {...iconProps} className={css("vss-Breadcrumb--item-leftIcon vss-HubBreadcrumb--item-leftIcon", iconProps.className)} />;
        }
        return null;
    }

    private _onItemClick = (item: IVssBreadcrumbItem): void => {
        this.setState(
            {
                pickerItemKey: item.key
            },
            () => {
                if (this.pickList) {
                    this.pickList.toggleDropdown();
                }
            }
        );
    };

    private onRenderItem = (item: IVssBreadcrumbItemInternal, defaultRenderer: (item: IBreadcrumbItem) => JSX.Element | null): JSX.Element | null => {
        const { presentationStyles, shouldUseFocusZone } = this.props;
        const { pickerItemKey } = this.state;
        // We want the text AND the icon to be clickable, but since the defaultRenderer attaches the onClick
        // to the text only, we make sure that doesn't even render an on-click and that the onclick goes to
        // the element that contains both the text and icon.
        const itemNoOnClick = { ...item } as IBreadcrumbItem;
        delete itemNoOnClick.onClick;
        delete itemNoOnClick.href;

        const icon = this._getIcon(item.leftIconProps);
        const originalDefaultRendered = defaultRenderer(itemNoOnClick);
        const originalClassName = originalDefaultRendered.props && originalDefaultRendered.props.className;
        let itemClass =
            presentationStyles & VssBreadcrumbStyles.smallSize
                ? "vss-Breadcrumb--itemText-small"
                : "vss-Breadcrumb--itemText vss-HubBreadcrumb--itemText";
        itemClass = css(itemClass, "ellipsis-text", !!(presentationStyles & VssBreadcrumbStyles.lightColor) && "vss-Breadcrumb--light");
        const clonedElementProps = {
            ...originalDefaultRendered.props,
            className: css(originalClassName, itemClass),
            style: originalDefaultRendered.props.style ? { ...originalDefaultRendered.props.style } : {}
        };
        if (this.props.maxItemWidth) {
            clonedElementProps.style.maxWidth = this.props.maxItemWidth;
        }
        const defaultRendered = React.cloneElement(originalDefaultRendered, clonedElementProps);

        const tabIndex = shouldUseFocusZone ? 0 : -2;
        const ariaDescribedById = getId("vss-breadcrumb-item-described-by");

        if (item.isHeaderPickerItem && item.itemPicker) {
            const iconVisible = !item.nonInteractive && !!(presentationStyles & VssBreadcrumbStyles.dividerLink);
            let onItemClick;
            if (!item.nonInteractive) {
                onItemClick = this._onItemClick.bind(this, item);
            }

            return (
                <div
                    ref={elem => {
                        if (elem) {
                            this._pinElements[item.key] = elem;
                            this._breadcrumbElements[item.key] = elem;
                        }
                    }}
                    className={css(
                        "vss-Breadcrumb--item vss-HubBreadcrumb--item",
                        !onItemClick && "no-click",
                        !!(presentationStyles & VssBreadcrumbStyles.minHeight) && "vss-Breadcrumb--item-min-height"
                    )}
                >
                    <Link
                        key={"BreadcrumbItem$" + item.key}
                        componentRef={elem => {
                            if (elem) {
                                this._linkElements[item.key] = elem;
                            }
                        }}
                        onClick={onItemClick}
                        href={item.href}
                        aria-haspopup={true}
                        aria-expanded={this.state.pickerIsOpen}
                        aria-label={item.ariaLabel}
                        aria-describedby={getSafeId(ariaDescribedById)}
                        tabIndex={tabIndex}
                        onFocus={shouldUseFocusZone ? undefined : this._onLinkFocus.bind(this, item.key)}
                    >
                        <div className="vss-Breadcrumb--itemContainer vss-HubBreadcrumb--itemContainer" role={item.role} aria-level={item.ariaLevel}>
                            {icon}
                            <Tooltip overflowOnly={true} text={item.text}>
                                {defaultRendered}
                            </Tooltip>
                            {iconVisible && this._getBreadcrumbIcon(item)}
                        </div>
                        <div id={getSafeId(ariaDescribedById)} className="vss-Breadcrumb--hiddenElement">
                            {item.ariaDescription}
                        </div>
                    </Link>
                    {pickerItemKey && pickerItemKey === item.key && this._renderPickerDropdown(item)}
                    {this._getIndicators(item)}
                </div>
            );
        } else if (item.onClick || item.href) {
            return (
                <div
                    ref={elem => {
                        if (elem) {
                            this._breadcrumbElements[item.key] = elem;
                        }
                    }}
                    className={css(
                        "vss-Breadcrumb--item vss-HubBreadcrumb--item",
                        !!(presentationStyles & VssBreadcrumbStyles.minHeight) && "vss-Breadcrumb--item-min-height"
                    )}
                >
                    <Link
                        componentRef={elem => {
                            if (elem) {
                                this._linkElements[item.key] = elem;
                            }
                        }}
                        key={"BreadcrumbItem$" + item.key}
                        onClick={item.onClick as any}
                        href={item.href}
                        aria-label={item.ariaLabel}
                        aria-describedby={getSafeId(ariaDescribedById)}
                        tabIndex={tabIndex}
                        onFocus={shouldUseFocusZone ? undefined : this._onLinkFocus.bind(this, item.key)}
                    >
                        <div className="vss-Breadcrumb--itemContainer">
                            {icon}
                            {defaultRendered}
                        </div>
                        <div id={getSafeId(ariaDescribedById)} className="vss-Breadcrumb--hiddenElement">
                            {item.ariaDescription}
                        </div>
                    </Link>
                </div>
            );
        } else {
            return (
                <div
                    ref={elem => {
                        if (elem) {
                            this._breadcrumbElements[item.key] = elem;
                        }
                    }}
                    className={css(
                        "vss-Breadcrumb--item vss-HubBreadcrumb--item",
                        !!(presentationStyles & VssBreadcrumbStyles.minHeight) && "vss-Breadcrumb--item-min-height"
                    )}
                    aria-label={item.ariaLabel}
                >
                    {icon}
                    {defaultRendered}
                </div>
            );
        }
    };

    private _onLinkFocus = (itemKey: string) => {
        if (this._focusedLinkIndex < 0) {
            this._focusedLinkIndex = findIndex(this.state.linkItems, item => item.key === itemKey);
        }
    };

    private _renderPickerDropdown(item: IVssBreadcrumbItemInternal): JSX.Element | null {
        const {
            pickListClassName,
            pickListWidth: userWidth,
            pickListMinWidth: minWidth,
            pickListMaxWidth: maxWidth,
            pickListMaxHeight: maxHeight
        } = this.props;
        const calculatedMinWidth =
            item.pickerWidth || userWidth || minWidth || (this._pinElements[item.key] && this._pinElements[item.key].clientWidth);
        const useFixedWidth: boolean = !maxWidth;
        const className = item.pickListClassName || pickListClassName;
        return (
            <PickerDropdown
                ref={pickList => {
                    this.pickList = pickList;
                }}
                item={item}
                getPinElement={() => this._pinElements[item.key]}
                width={useFixedWidth && calculatedMinWidth} // Use fixed width when maxWidth not set
                minWidth={!useFixedWidth && calculatedMinWidth} // When not use fixed width, set minWidth
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                pickListClassName={className}
                onPickListClosed={() => this.setState({ pickerIsOpen: false })}
                onPickListOpened={() => this.setState({ pickerIsOpen: true })}
            />
        );
    }

    private _getBreadcrumbIcon(item: IVssBreadcrumbItemInternal): JSX.Element {
        const { pickerItemKey } = this.state;
        const { dividerType } = this.props;

        if (dividerType === VssBreadcrumbDividerType.ForwardSlash) {
            return <div className="vss-Breadcrumb--forward-slash">/</div>;
        }
        return (
            <VssIcon
                iconName={item.isHeaderPickerItem || (item.key === pickerItemKey && this.state.pickerIsOpen) ? "ChevronDown" : "ChevronRight"}
                iconType={VssIconType.fabric}
                key={"RightIcon$" + item.key}
                className={css(
                    "vss-Breadcrumb--item-dropdown vss-HubBreadcrumb--item-dropdown",
                    item.isHeaderPickerItem && "vss-Breadcrumb--item-dropdown-with-picker vss-HubBreadcrumb--item-dropdown-with-picker"
                )}
            />
        );
    }

    private _getCustomDivider = (dividerProps: IDividerAsProps): JSX.Element => {
        const dividerItem = dividerProps.item as IVssBreadcrumbItemInternal;
        const { presentationStyles } = this.props;

        if (presentationStyles !== undefined && !(presentationStyles & VssBreadcrumbStyles.dividerLink)) {
            return (
                <div
                    ref={elem => {
                        if (elem) {
                            this._breadcrumbElements["BreadcrumbItemDivider$" + dividerItem.key] = elem;
                        }
                    }}
                    key={"BreadcrumbItemDivider$" + dividerItem.key}
                    className={css(
                        "vss-Breadcrumb--divider vss-HubBreadcrumb--divider",
                        !!(presentationStyles & VssBreadcrumbStyles.extraPadding) && "vss-Breadcrumb--divider-extra-padding",
                        !!(presentationStyles & VssBreadcrumbStyles.lightColor) && "vss-Breadcrumb--divider-light"
                    )}
                >
                    {this._getBreadcrumbIcon(dividerItem)}
                </div>
            );
        }

        return (
            <div
                key={"BreadcrumbItemDivider$" + dividerItem.key}
                className="vss-Breadcrumb--divider-container"
                ref={elem => {
                    if (elem) {
                        this._pinElements[dividerItem.key] = elem;
                        this._breadcrumbElements["BreadcrumbItemDivider$" + dividerItem.key] = elem;
                    }
                }}
            >
                {this._getDividerButtonOrIcon(dividerItem)}
            </div>
        );
    };

    private _getDividerButtonOrIcon(dividerItem: IVssBreadcrumbItemInternal): JSX.Element {
        const { pickerItemKey } = this.state;
        const breadcrumbIcon = this._getBreadcrumbIcon(dividerItem);
        return !!dividerItem.itemPicker ? (
            <Link
                onClick={this._onItemClick.bind(this, dividerItem)}
                aria-expanded={this.state.pickerIsOpen}
                aria-label={dividerItem.itemPicker.ariaLabel}
                className={css("vss-Breadcrumb--divider vss-Breadcrumb--divider-link")}
            >
                {breadcrumbIcon}
                {pickerItemKey && pickerItemKey === dividerItem.key && dividerItem.itemPicker && this._renderPickerDropdown(dividerItem)}
            </Link>
        ) : (
            breadcrumbIcon
        );
    }

    private _getIndicators(item: IVssBreadcrumbItemInternal): JSX.Element {
        const headerItemPicker = item.isHeaderPickerItem && item.itemPicker;
        if (
            headerItemPicker &&
            headerItemPicker.selectedItem &&
            headerItemPicker.selectedItemIndicators &&
            headerItemPicker.selectedItemIndicators.length
        ) {
            return (
                <ItemIndicatorSet
                    indicators={headerItemPicker.selectedItemIndicators.map(indicator => indicator.getItemIndicator(headerItemPicker.selectedItem))}
                />
            );
        } else {
            return null;
        }
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        const { linkItems } = this.state;

        if (event.which === KeyCode.leftArrow && this._focusedLinkIndex > 0) {
            if (this.props.extraContentSelected) {
                this.props.onExtraContentSelectionChanged(false);
            }
            this._linkElements[linkItems[--this._focusedLinkIndex].key].focus();
            event.preventDefault();
        }
        if (event.which === KeyCode.rightArrow && this._focusedLinkIndex >= 0) {
            if (this._focusedLinkIndex < linkItems.length - 1) {
                this._linkElements[linkItems[++this._focusedLinkIndex].key].focus();
                event.preventDefault();
            } else if (
                this._focusedLinkIndex === linkItems.length - 1 &&
                this._isExtraContentSelectable() &&
                this.props.onExtraContentSelectionChanged
            ) {
                this.props.onExtraContentSelectionChanged(true);
                this._focusedLinkIndex++;
                event.preventDefault();
            } else if (this.props.extraContentSelected) {
                this.props.onExtraContentSelectionChanged(false);
            }
        }
        if (event.which === KeyCode.downArrow && this._focusedLinkIndex >= 0 && !this.props.extraContentSelected) {
            this._onItemClick(linkItems[this._focusedLinkIndex]);
        }
    };

    private _onBlur = () => {
        if (!this.state.pickerIsOpen && !this.props.extraContentSelected) {
            this._focusedLinkIndex = -1;
        }
    };

    /**
     * Sort our breadcrumb items
     * @param items
     */
    private static _arrangeItems(items: IVssBreadcrumbItem[]): IVssBreadcrumbItem[] {
        // Build map of what is currently in the list of breadcrumb items
        const existingItemsMap: { [id: string]: IVssBreadcrumbItem } = {};
        // If exisiting items have the same id and different priorities, the higher priority item will be shown.
        items.forEach(item => {
            if (!existingItemsMap[item.key] || (existingItemsMap[item.key].priority || 0) <= (item.priority || 0)) {
                existingItemsMap[item.key] = item;
            }
        });

        const newItems: IVssBreadcrumbItem[] = [];
        Object.keys(existingItemsMap).forEach(key => {
            newItems.push(existingItemsMap[key]);
        });

        const shouldSortItems = newItems.some(x => x.rank! >= 0);

        if (shouldSortItems) {
            // This is not a stable sort. If we pass items with the same rank, we may not always get them back in the
            // same order. We have a stable sort in vssf webplatform, but we're not taking a dependency there.
            newItems.sort((a, b) => {
                const aRank = a.rank || Number.MAX_VALUE;
                const bRank = b.rank || Number.MAX_VALUE;
                return aRank - bRank;
            });
        }

        return newItems;
    }

    private _dataDidRender = (renderedData: IBreadcrumbData) => {
        const { dataDidRender } = this.props;

        if (dataDidRender) {
            let itemsWidth = 0;
            if (renderedData.renderedItems.length === 1) {
                itemsWidth = this._breadcrumbElements[renderedData.renderedItems[0].key].clientWidth;
            } else {
                Object.keys(this._breadcrumbElements).map((key, index) => {
                    itemsWidth += this._breadcrumbElements[key].clientWidth;
                });
            }
            dataDidRender(renderedData, itemsWidth);
        }
    };

    private _isExtraContentSelectable(): boolean {
        const extraContentContainer = document.getElementById(getSafeIdSelector("breadcrumb-extra-content"));
        if (extraContentContainer) {
            return !!extraContentContainer.querySelector('[tabindex="-2"]');
        }
        return false;
    }

    public render() {
        const {
            ariaLabel,
            className,
            maxDisplayedItems = DEFAULT_MAXDISPLAY_ITEMS,
            onReduceData,
            overflowIndex,
            shouldUseFocusZone,
            extraContent
        } = this.props;

        return (
            <div
                className={css("vss-Breadcrumb--container vss-HubBreadcrumb--container", className)}
                onKeyDown={shouldUseFocusZone ? undefined : this._onKeyDown}
                onBlur={shouldUseFocusZone ? undefined : this._onBlur}
            >
                <InternalBreadcrumb
                    onReduceData={onReduceData ? onReduceData : data => undefined}
                    dataDidRender={this._dataDidRender}
                    items={this.state.displayedItems}
                    className={css("vss-Breadcrumb vss-HubBreadcrumb")}
                    onRenderItem={this.onRenderItem}
                    ariaLabel={ariaLabel || VSSUIResources.BreadcrumbAriaLabel}
                    maxDisplayedItems={maxDisplayedItems}
                    overflowIndex={overflowIndex}
                    dividerAs={this._getCustomDivider}
                    shouldUseFocusZone={shouldUseFocusZone}
                    extraContent={extraContent}
                />
            </div>
        );
    }
}

/** @deprecated */
export interface IBreadcrumbData {
    props: IInternalBreadcrumbProps;
    renderedItems: IInternalBreadcrumbItem[];
    renderedOverflowItems: IInternalBreadcrumbItem[];
}

const OVERFLOW_KEY = "overflow";
const nullFunction = (): null => null;

class InternalBreadcrumb extends React.Component<IInternalBreadcrumbProps, any> {
    public static defaultProps: IInternalBreadcrumbProps = {
        items: [],
        maxDisplayedItems: 999,
        overflowIndex: 0
    };

    private _focusZone = createRef<FocusZone>();

    constructor(props: IInternalBreadcrumbProps) {
        super(props);

        InternalBreadcrumb._validateProps(props);
        this.state = {};
    }

    public render(): JSX.Element {
        const { onReduceData, dataDidRender, overflowIndex, maxDisplayedItems, items } = this.props;
        const renderedItems = [...items];
        const renderedOverflowItems = renderedItems.splice(overflowIndex!, renderedItems.length - maxDisplayedItems!);
        const breadCrumbData: IBreadcrumbData = {
            props: this.props,
            renderedItems,
            renderedOverflowItems
        };

        return (
            <ResizeGroup
                onRenderData={this._onRenderBreadcrumb}
                onReduceData={onReduceData}
                dataDidRender={dataDidRender}
                data={breadCrumbData}
                className={"vss-Breadcrumb--resizeGroup"}
            />
        );
    }

    public static getDerivedStateFromProps(props: IInternalBreadcrumbProps, state: any): any {
        InternalBreadcrumb._validateProps(props);
        return state;
    }

    private _onRenderBreadcrumb = (data: IBreadcrumbData) => {
        const {
            ariaLabel,
            dividerAs: DividerType = Icon as React.ReactType<IDividerAsProps>,
            onRenderItem = this._onRenderItem,
            overflowAriaLabel,
            overflowIndex,
            className,
            shouldUseFocusZone,
            extraContent
        } = data.props;
        const { renderedOverflowItems, renderedItems } = data;

        const contextualItems = renderedOverflowItems.map((item, index) => ({
            name: item.text,
            key: item.key,
            onClick: item.onClick ? this._onBreadcrumbClicked.bind(this, item) : null,
            href: item.href
        }));

        // Find index of last rendered item so the divider icon
        // knows not to render on that item
        const lastItemIndex = renderedItems.length - 1;
        const hasOverflowItems = renderedOverflowItems && renderedOverflowItems.length !== 0;

        const itemElements: JSX.Element[] = renderedItems.map((item, index) => (
            <li className="internal-breadcrumb--listItem" key={item.key || String(index)}>
                {onRenderItem(item, this._onRenderItem)}
                {(index !== lastItemIndex || (hasOverflowItems && index === overflowIndex! - 1)) && (
                    <DividerType className="internal-breadcrumb--chevron" iconName="ChevronRight" item={item} />
                )}
            </li>
        ));

        if (hasOverflowItems) {
            itemElements.splice(
                overflowIndex!,
                0,
                <li className="internal-breadcrumb--overflow" key={OVERFLOW_KEY}>
                    <IconButton
                        className="internal-breadcrumb--overflowButton"
                        iconProps={{ iconName: "More" }}
                        role="button"
                        aria-haspopup="true"
                        ariaLabel={overflowAriaLabel}
                        onRenderMenuIcon={nullFunction}
                        menuProps={{
                            items: contextualItems,
                            directionalHint: DirectionalHint.bottomLeftEdge
                        }}
                    />
                    {overflowIndex !== lastItemIndex + 1 && (
                        <DividerType
                            className="internal-breadcrumb--chevron"
                            iconName="ChevronRight"
                            item={renderedOverflowItems[renderedOverflowItems.length - 1]}
                        />
                    )}
                </li>
            );
        }

        if (extraContent) {
            itemElements.push(
                <li key="breadcrumb-extra-content" id={getSafeId("breadcrumb-extra-content")}>
                    {extraContent}
                </li>
            );
        }

        let listComponent = <ol className="internal-breadcrumb--list">{itemElements}</ol>;

        if (shouldUseFocusZone) {
            listComponent = (
                <FocusZone componentRef={this._focusZone} direction={FocusZoneDirection.horizontal}>
                    {listComponent}
                </FocusZone>
            );
        }

        return (
            <div className={className} role="navigation" aria-label={ariaLabel}>
                {listComponent}
            </div>
        );
    };

    private _onRenderItem = (item: IInternalBreadcrumbItem) => {
        return <span className="internal-breadcrumb--item">{item.text}</span>;
    };

    private _onBreadcrumbClicked = (item: IInternalBreadcrumbItem, ev: React.MouseEvent<HTMLElement>) => {
        if (item.onClick) {
            item.onClick(ev, item);
        }
    };

    /**
     * Validate incoming props
     * @param props Props to validate
     */
    private static _validateProps(props: IInternalBreadcrumbProps): void {
        const { maxDisplayedItems, overflowIndex, items } = props;
        if (
            overflowIndex! < 0 ||
            (maxDisplayedItems! > 1 && overflowIndex! > maxDisplayedItems! - 1) ||
            (items.length > 0 && overflowIndex! > items.length - 1)
        ) {
            throw new Error("Breadcrumb: overflowIndex out of range");
        }
    }
}
